import { encodeJpegWithinBudget, loadImage } from '@/utils/compress-jpeg';

/** The two pens of a Damage marking. They carry no meaning; two so a stroke shows on any paint. */
export type MarkingColor = 'red' | 'green';

export const MARKING_COLORS: Record<MarkingColor, string> = {
  red: '#ef4444',
  green: '#22c55e',
};

export type MarkingPoint = { x: number; y: number };

/** One freehand stroke, in pixels of the photo it is drawn on. */
export type MarkingStroke = { color: MarkingColor; points: MarkingPoint[] };

/** Pen width relative to the photo's longest edge, so a stroke looks the same at any resolution. */
const STROKE_WIDTH_RATIO = 0.008;

export function getMarkingStrokeWidth(width: number, height: number): number {
  return Math.max(4, Math.round(Math.max(width, height) * STROKE_WIDTH_RATIO));
}

/**
 * Converts a point of the frame the photo fills (CSS `object-cover`: scaled to cover, centred,
 * the overflow cropped) into a pixel of the photo itself.
 */
export function toPhotoPoint(
  point: { x: number; y: number },
  frame: { width: number; height: number },
  photo: { width: number; height: number },
): MarkingPoint {
  const scale = Math.max(frame.width / photo.width, frame.height / photo.height);
  const cropX = (photo.width * scale - frame.width) / 2;
  const cropY = (photo.height * scale - frame.height) / 2;
  return {
    x: Math.round((point.x + cropX) / scale),
    y: Math.round((point.y + cropY) / scale),
  };
}

/** Points as an SVG polyline `points` attribute; a single tap is doubled so it shows as a dot. */
export function toPolylinePoints(points: MarkingPoint[]): string {
  const drawn = points.length === 1 ? [points[0], points[0]] : points;
  return drawn.map((point) => `${point.x},${point.y}`).join(' ');
}

/**
 * Draws the strokes into the photo and encodes it again as JPEG. The unmarked original is not
 * kept (ADR 0021).
 */
export async function burnMarkingIntoPhoto(photo: File, strokes: MarkingStroke[]): Promise<File> {
  if (!strokes.length) return photo;
  const image = await loadImage(photo);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');
  ctx.drawImage(image, 0, 0, width, height);
  ctx.lineWidth = getMarkingStrokeWidth(width, height);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const stroke of strokes) {
    const [first, ...rest] = stroke.points;
    ctx.strokeStyle = MARKING_COLORS[stroke.color];
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const point of rest.length ? rest : [first]) ctx.lineTo(point.x, point.y);
    ctx.stroke();
  }
  const marked = await encodeJpegWithinBudget(canvas, width, height);
  return new File([marked], photo.name, { type: 'image/jpeg' });
}
